import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash-es';
import { filter, take } from 'rxjs/operators';
import { assert } from '../../../../../../core/assert/assert';
import { TimeBlockDayOrWeekVariableIntervalType } from '../../../../../../core/models/timeblock/time-block-view-type.model';
import { CalendarGeometryService } from '../../../../services/calendar-geometry.service';
import { CalendarService } from '../../../../services/calendar.service';
import { TimeCoordinateMappingService } from '../../../../time-mapping/time-coordinate-mapping.service';
import { DragPart } from '../../interaction/movement/drag-and-resize/drag-part';
import { ResizeData } from '../../interaction/movement/drag-and-resize/resizing/resize-view-controller/vertical-day-or-week-resize-controller.service';
import { TimeBlockDragResizeControllerService } from '../../interaction/movement/drag-and-resize/time-block-drag-resize-handling/time-block-drag-resize-controller.service';
import { TimeBlockService } from '../../services/time-block.service';
import { ITimeBlockComponentItem } from '../../types/time-block-component-item-types';
import { TimeBlockGeometryCalculator } from './time-block-geometry-calculator';

@Injectable()
export class TimeBlockGeometryService {
  private calculator: TimeBlockGeometryCalculator;

  constructor(
    private readonly calendarService: CalendarService,
    private readonly timeBlockDragResizeControllerService: TimeBlockDragResizeControllerService,
    private readonly calendarGeometryService: CalendarGeometryService,
    private readonly timeCoordinateMapperService: TimeCoordinateMappingService,
    private readonly timeBlockService: TimeBlockService,
  ) {}

  /**
   *
   * Calculate the time block geometry during resizing.
   */
  public calculateVerticalResizeBlockGeometry(
    clonedResizeData: ResizeData,
    snapDurationInPixels: number,
    calendarBodyHeight: number,
  ): void {
    const geometryData =
      this.timeBlockDragResizeControllerService.TransformationTimeBlock.timeBlockModel
        .timeBlockViewType.geometryData;

    const { top, bottom, height } = geometryData;

    const snapDelta = (delta: number): number =>
      Math.abs(delta) < snapDurationInPixels ? Math.sign(delta) * snapDurationInPixels : delta;

    const getTopDelta = (): number => {
      const delta = snapDelta(top - clonedResizeData.dragEdgePos.y);
      return top - delta < 0 ? top : delta;
    };

    const getBottomDelta = (): number =>
      snapDelta(calendarBodyHeight - bottom - clonedResizeData.dragEdgePos.y);

    let dragPart = clonedResizeData.dragPart;
    let delta = dragPart === DragPart.Top ? getTopDelta() : getBottomDelta();

    const minimumTimeBlockDuration =
      this.calendarService.model.calendarProperties.minimumTimeBlockDuration;
    // Very rare case: The drag part is not defined yet, but the mouse cursor is above the newly created time block.
    // This can happen when the user clicked into a daylight saving hour (winter-time to summer-time). So skip this resize cycle.
    if (delta > minimumTimeBlockDuration && dragPart === DragPart.TopOrBottom) {
      return;
    }

    // Flip resize direction if the block would collapse to zero height. This can happen if a user moves the mouse really, really quickly.
    if (dragPart === DragPart.Top) {
      dragPart = height + delta === 0 ? DragPart.Bottom : DragPart.Top;
    } else {
      dragPart = height - delta === 0 ? DragPart.Top : DragPart.Bottom;
    }

    // Calculate how much the time block should be resized.
    delta = dragPart === DragPart.Top ? getTopDelta() : getBottomDelta();

    if (delta === 0) {
      return;
    }

    const newHeightTop = height + delta;
    if (dragPart === DragPart.Top && newHeightTop < minimumTimeBlockDuration) {
      return;
    }

    const newHeightBottom = height - delta;
    if (dragPart === DragPart.Bottom && newHeightBottom < minimumTimeBlockDuration) {
      return;
    }

    if (dragPart === DragPart.Top) {
      geometryData.top = top - delta;
      geometryData.height = newHeightTop;
      geometryData.calendarBottomEdgeToTimeBlockTopEdge = bottom + height + delta;
    } else {
      geometryData.bottom = bottom + delta;
      geometryData.height = newHeightBottom;
      geometryData.calendarTopEdgeToTimeBlockBottomEdge = top + height - delta;
    }

    assert(geometryData.top >= 0, `Top is invalid: ${geometryData.top}`);
    assert(geometryData.bottom >= 0, `Bottom is invalid: ${geometryData.bottom}`);
  }

  /**
   * Calculate the time block geometry for each block. Wait until pixelsPerMinute have been calculated after page reload.
   */
  public calculateInitialTimeBlockGeometry(timeBlock: ITimeBlockComponentItem): void {
    this.calculator = new TimeBlockGeometryCalculator(
      this.calendarService,
      this.calendarGeometryService,
      this.timeCoordinateMapperService,
      this.timeBlockService,
    );

    // Inner day time block
    if (
      timeBlock.timeBlockModel.timeBlockViewType instanceof TimeBlockDayOrWeekVariableIntervalType
    ) {
      this.timeCoordinateMapperService.pixelsPerMinuteCalculated$
        .pipe(
          take(1),
          filter((ppm) => !!ppm),
        )
        .subscribe(() => {
          this.calcAndEmitInitialGeometryData(timeBlock);
        });
    } else {
      // Full day type
      this.calcAndEmitInitialGeometryData(timeBlock);
    }
  }

  private calcAndEmitInitialGeometryData(timeBlock: ITimeBlockComponentItem): void {
    const clonedTimeBlockAlignmentModel = cloneDeep(timeBlock.timeBlockModel.timeBlockViewType);
    if (timeBlock.timeBlockModel.fixInterval !== null) {
      // For week view and month view time blocks that can be resized horizontally
      clonedTimeBlockAlignmentModel.geometryData =
        this.calculator.initializeFixIntervalTimeBlockGeometry(timeBlock);
    } else {
      // For week view or month view inner day time blocks that can be resized vertically
      clonedTimeBlockAlignmentModel.geometryData =
        this.calculator.initializeVariableIntervalTimeBlockGeometry(timeBlock);
    }

    timeBlock.timeBlockModel.timeBlockViewType = clonedTimeBlockAlignmentModel;
    this.calendarService.emitCalendarChange(this.calendarService.model, [], timeBlock);
  }
}
